import {plural} from "@lingui/core/macro";

const SECONDS_PER_HOUR = 3600;

/**
 * Left-pads a number with '0's. Doesn't differentiate between floats and ints.
 * `minLength` represents the minimum number of digits in the output. A value
 * of 3 might return "007", for example.
 */
function pad(n: number | string, minLength: number) {
    const padLength = Math.max(minLength - `${n}`.length, 0);
    return new Array(padLength).fill(0).join("") + n;
}

/**
 * Splits seconds into hours, minutes, and seconds.
 */
function durationFromSeconds(totalSeconds: number) {
    const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
    const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return {hours, minutes, seconds};
}

/**
 * Takes a number of seconds and returns the time in hours:minutes:seconds or
 * just minutes:seconds form.
 *
 * This function's highest delimiter is hours. You may pass in times larger
 * than 24 hours and it will still be displayed as hour:minute:day.
 */
export function formatTimeDisplay(totalSeconds: number): string {
    const {hours, minutes, seconds} = durationFromSeconds(totalSeconds);
    const paddedMinutes = pad(minutes, 2);
    const paddedSeconds = pad(seconds, 2);

    return `${hours ? `${hours}:${paddedMinutes}` : minutes}:${paddedSeconds}`;
}

/**
 * Takes a time in seconds and returns the time in words so that it can be
 * read by a screen reader. Used in aria-label.
 */
export function formatTimeDisplayAria(totalSeconds: number): string {
    const {hours, minutes, seconds} = durationFromSeconds(totalSeconds);
    const output: Array<any> = [];

    if (hours) {
        output.push(
            plural(hours, {
                one: "# hour",
                other: "# hours",
            }),
        );
    }
    if (minutes) {
        output.push(
            plural(minutes, {
                one: "# minute",
                other: "# minutes",
            }),
        );
    }
    if (seconds) {
        output.push(
            plural(seconds, {
                one: "# second",
                other: "# seconds",
            }),
        );
    }

    return output.join(" ");
}
